<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { Button } from '@/components/ui/button'
  import InputField from '@/components/admin/form/InputField.vue'
  import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import { DownloadIcon, RefreshCwIcon } from 'lucide-vue-next'
  import { WordDocumentTemplatesService } from '@/types/generated/services.gen'
  import { VuePDF } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import { useDebounceFn } from '@/composables/useDebounce'
  import type { SuperUserWordDocumentTemplate } from '@/types/generated'

  const route = useRoute()
  const router = useRouter()
  const { toast } = useToast()
  const templateId = route.params.uuid as string
  const activeTab = ref(route.query.use_ai_template === 'true' ? 'ai' : 'living')
  const useAiTemplate = computed(() => activeTab.value === 'ai')

  const { t } = useTranslation({
    en: {
      pageTitle: 'Validate',
      dashboard: 'Dashboard',
      wordTemplates: 'Word Templates',
      backToReview: 'Back to Review',
      generateValidationData: 'Generate Validation Data',
      downloadDocument: 'Download Document',
      validationData: 'Validation Data',
      preview: 'Preview',
      generatingValidationData: 'Loading preview...',
      templateName: 'Kündigungsschreiben Mietvertrag',
      placeholders: 'Placeholders',
      blockVariables: 'Block Variables',
      refresh: 'Refresh',
      refreshing: 'Refreshing...',
      useAiTemplate: 'Use AI Template',
      originalTemplate: 'Original',
      aiTemplate: 'AI Generated',
      livingDoc: 'Current docx template',
      aiDoc: 'AI generated docx template'
    },
    de: {
      pageTitle: 'Validierung',
      dashboard: 'Dashboard',
      wordTemplates: 'Word Vorlagen',
      backToReview: 'Zurück zur Überprüfung',
      generateValidationData: 'Validierungsdaten generieren',
      downloadDocument: 'Dokument herunterladen',
      validationData: 'Validierungsdaten',
      preview: 'Vorschau',
      generatingValidationData: 'Vorschau wird geladen...',
      templateName: 'Kündigungsschreiben Mietvertrag',
      placeholders: 'Platzhalter',
      blockVariables: 'Block-Variablen',
      refresh: 'Aktualisieren',
      refreshing: 'Aktualisiere...',
      useAiTemplate: 'KI-Vorlage verwenden',
      originalTemplate: 'Original',
      aiTemplate: 'KI-generiert',
      livingDoc: 'Aktuelle docx-Vorlage',
      aiDoc: 'KI-generierte docx-Vorlage'
    }
  })

  interface PlaceholderData {
    key: string
    value: string
    originalValue?: string
    isBlockVariable?: boolean
  }

  const isLoading = ref(false)
  const isRefreshing = ref(false)
  const hasGeneratedData = ref(false)
  const hasAiTemplate = ref(false)
  const placeholders = ref<PlaceholderData[]>([])
  const templateName = ref<string>('Loading...')
  const validationData = ref<Record<string, any>>({})
  const error = ref<string | null>(null)
  const pendingUpdates = ref<Record<string, any>>({})

  const arrayBuffer = ref<Uint8Array | null>(null)
  const { pdf, pages } = usePDF(arrayBuffer)
  const pdfLoaded = ref<boolean>(false)
  const currentPage = ref<number>(1)

  const breadcrumbItems = computed(() => [
    { title: t('dashboard'), to: ROUTES.ADMIN.DASHBOARD },
    { title: t('wordTemplates'), to: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW },
    {
      title: templateName.value,
      to: {
        path: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW_DETAIL.replace(':uuid', templateId),
        query: { use_ai_template: useAiTemplate.value ? 'true' : 'false' }
      }
    },
    { title: t('pageTitle') }
  ])

  const initializeValidationData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        isRefreshing.value = true
      } else {
        isLoading.value = true
      }
      error.value = null

      const response = await WordDocumentTemplatesService.wordDocumentTemplatesValidateRetrieve({
        uuid: templateId,
        useAiTemplate: useAiTemplate.value
      })

      if (response) {
        const allPlaceholders: PlaceholderData[] = []
        const placeholderKeys = response.placeholders || []
        const responseValidationData = response.validation_data as Record<string, any>

        placeholderKeys.forEach((key: string) => {
          const value = responseValidationData[key]
          const isBoolean = typeof value === 'boolean'

          allPlaceholders.push({
            key: key,
            value: String(value || ''),
            originalValue: String(value || ''),
            isBlockVariable: isBoolean
          })
        })

        placeholders.value = allPlaceholders

        templateName.value = response.template_name || 'Template'

        validationData.value = responseValidationData
        hasGeneratedData.value = true

        // Process preview blob if available
        if (response.preview_blob) {
          const binaryString = atob(response.preview_blob)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          arrayBuffer.value = bytes
          currentPage.value = 1
          pdfLoaded.value = true
        }
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to initialize validation data'
      toast({
        variant: 'destructive',
        description: error.value || undefined
      })
    } finally {
      isLoading.value = false
      isRefreshing.value = false
    }
  }

  const refreshValidationData = async () => {
    isRefreshing.value = true
    error.value = null

    const response = await WordDocumentTemplatesService.wordDocumentTemplatesValidateRetrieve({
      uuid: templateId,
      useAiTemplate: useAiTemplate.value,
      forceRefresh: true
    })

    if (response) {
      const allPlaceholders: PlaceholderData[] = []

      const responseValidationData = response.validation_data as Record<string, any>
      const placeholderKeys = Object.keys(responseValidationData)

      placeholderKeys.forEach((key: string) => {
        const value = responseValidationData[key]
        const isBoolean = typeof value === 'boolean'

        allPlaceholders.push({
          key: key,
          value: String(value || ''),
          originalValue: String(value || ''),
          isBlockVariable: isBoolean
        })
      })

      placeholders.value = allPlaceholders
      validationData.value = responseValidationData
      hasGeneratedData.value = true

      // Update the preview with the new data
      if (response.preview_blob) {
        const binaryString = atob(response.preview_blob)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        arrayBuffer.value = bytes
        currentPage.value = 1
        pdfLoaded.value = true
      }
    }

    isRefreshing.value = false
  }

  const debouncedUpdate = useDebounceFn(async () => {
    if (Object.keys(pendingUpdates.value).length === 0) return

    try {
      const response =
        await WordDocumentTemplatesService.wordDocumentTemplatesValidatePartialUpdate({
          uuid: templateId,
          requestBody: {
            placeholder_values: pendingUpdates.value,
            use_ai_template: useAiTemplate.value
          }
        })

      if (response) {
        validationData.value = response.validation_data as Record<string, any>

        placeholders.value.forEach((p) => {
          if (pendingUpdates.value[p.key] !== undefined) {
            p.originalValue = p.value
          }
        })

        pendingUpdates.value = {}

        // Process preview blob if available
        if (response.preview_blob) {
          const binaryString = atob(response.preview_blob)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          arrayBuffer.value = bytes
          currentPage.value = 1
          pdfLoaded.value = true
        }
      }
    } catch (err: any) {
      toast({
        variant: 'destructive',
        description: 'Failed to update fields'
      })
    }
  }, 1000)

  watch(
    () => placeholders.value,
    (newPlaceholders) => {
      if (!hasGeneratedData.value) return

      const updates: Record<string, any> = {}
      newPlaceholders.forEach((p) => {
        const currentValue = p.isBlockVariable ? p.value === 'true' : p.value
        if (validationData.value[p.key] !== currentValue) {
          updates[p.key] = currentValue
        }
      })

      if (Object.keys(updates).length > 0) {
        pendingUpdates.value = { ...pendingUpdates.value, ...updates }
        debouncedUpdate()
      }
    },
    { deep: true }
  )

  const downloadDocument = async () => {
    try {
      const response = await WordDocumentTemplatesService.wordDocumentTemplatesValidateCreate({
        uuid: templateId,
        requestBody: {
          use_ai_template: useAiTemplate.value
        }
      })

      if (response?.document_blob) {
        // Decode base64 to binary
        const binaryString = atob(response.document_blob)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Create blob and download
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        })

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = response.filename || `${templateName.value}_validated.docx`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        toast({
          description: 'Document downloaded successfully'
        })
      }
    } catch (err: any) {
      toast({
        variant: 'destructive',
        description: err.message || 'Failed to download document'
      })
    }
  }

  const onPdfLoaded = (_eventData: any) => {
    pdfLoaded.value = true
  }

  const setPage = (page: number) => {
    currentPage.value = page
  }

  onMounted(async () => {
    const template = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
      uuid: templateId
    })) as SuperUserWordDocumentTemplate
    hasAiTemplate.value = !!template.ai_docx_template

    await initializeValidationData()
  })

  watch(activeTab, async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await router.replace({
        query: { ...route.query, use_ai_template: newValue === 'ai' ? 'true' : 'false' }
      })

      if (hasGeneratedData.value) {
        await initializeValidationData(true)
      }
    }
  })

  // Sync tab from URL when query changes without remount (e.g. browser back/forward, shared link)
  watch(
    () => route.query.use_ai_template,
    (queryValue) => {
      const tab = queryValue === 'true' ? 'ai' : 'living'
      if (activeTab.value !== tab) activeTab.value = tab
    }
  )

  watch(
    () => pdf.value,
    (newPdf) => {
      if (newPdf && !pdfLoaded.value) {
        pdfLoaded.value = true
      }
    }
  )
</script>

<template>
  <div class="flex h-[calc(100vh-80px)] flex-col gap-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <div class="flex flex-shrink-0 items-center justify-between gap-x-5">
      <Breadcrumbs :items="breadcrumbItems" />
      <div class="flex items-center justify-end gap-x-3">
        <Button
          variant="secondary"
          :disabled="isRefreshing || !hasGeneratedData"
          @click="refreshValidationData"
        >
          <RefreshCwIcon
            class="mr-2 h-4 w-4"
            :class="{ 'animate-spin': isRefreshing }"
          />
          {{ isRefreshing ? t('refreshing') : t('refresh') }}
        </Button>
        <Button
          variant="default"
          :disabled="!hasGeneratedData"
          @click="downloadDocument"
        >
          <DownloadIcon class="mr-2 h-4 w-4" />
          {{ t('downloadDocument') }}
        </Button>
      </div>
    </div>

    <div
      v-if="error"
      class="flex-shrink-0 rounded-lg border border-red-200 bg-red-50 p-4"
    >
      <p class="text-sm text-red-800">{{ error }}</p>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-5 md:flex-row">
      <div
        class="w-3/10 flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm dark:border-neutral-700
          dark:bg-neutral-800 md:min-h-0"
      >
        <div class="flex-shrink-0 p-5 pb-0">
          <h2 class="text-xl font-semibold">{{ t('validationData') }}</h2>
        </div>

        <div class="flex min-h-0 w-full flex-1 flex-col p-5 pt-4">
          <Tabs
            default-value="placeholders"
            class="flex h-full w-full flex-col"
          >
            <TabsList class="grid w-full flex-shrink-0 grid-cols-2">
              <TabsTrigger value="placeholders">{{ t('placeholders') }}</TabsTrigger>
              <TabsTrigger value="blockVariables">{{ t('blockVariables') }}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="placeholders"
              class="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto"
            >
              <div
                v-for="placeholder in placeholders.filter((p) => !p.isBlockVariable)"
                :key="placeholder.key"
                class="space-y-2"
              >
                <div class="ml-1 mr-3 flex items-center">
                  <div class="flex-1">
                    <InputField
                      :id="placeholder.key"
                      v-model="placeholder.value"
                      class=""
                      :name="placeholder.key"
                      :label="placeholder.key"
                      :placeholder="placeholder.originalValue"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="blockVariables"
              class="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto"
            >
              <div
                v-for="placeholder in placeholders.filter((p) => p.isBlockVariable)"
                :key="placeholder.key"
                class="space-y-2"
              >
                <div class="ml-1 mr-3 flex items-center gap-2">
                  <div class="flex-1">
                    <label class="mb-2 block text-sm font-medium text-gray-700">
                      {{ placeholder.key }}
                    </label>
                    <select
                      :id="placeholder.key"
                      v-model="placeholder.value"
                      :name="placeholder.key"
                      class="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800
                        placeholder:text-gray-500 focus:z-10 focus:border-purple-600 focus:ring-purple-600"
                    >
                      <option value="">Select...</option>
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div
        class="md:w-7/10 flex w-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800 md:min-h-0"
      >
        <div class="flex flex-shrink-0 flex-col gap-3 p-5 pb-0">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">{{ t('preview') }}</h2>
            <span
              v-if="Object.keys(pendingUpdates).length > 0"
              class="text-xs text-orange-600"
            >
              • Rendering changes...
            </span>
          </div>

          <!-- Tabs for template type -->
          <Tabs
            v-if="hasAiTemplate"
            :default-value="activeTab"
            class="w-full"
            @update:model-value="(value) => (activeTab = value)"
          >
            <TabsList class="grid w-full grid-cols-2">
              <TabsTrigger value="living">
                {{ t('livingDoc') }}
              </TabsTrigger>
              <TabsTrigger value="ai">
                {{ t('aiDoc') }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div class="flex min-h-0 flex-1 flex-col p-5 pt-4">
          <SkeletonLoader
            v-if="!hasGeneratedData && !(pdfLoaded && pdf)"
            class="flex h-full w-full flex-col items-center justify-center bg-gray-100"
          >
            <div class="text-center">
              <div
                class="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-purple-700"
              ></div>
              <p class="text-gray-600">
                {{ t('generatingValidationData') }}
              </p>
            </div>
          </SkeletonLoader>

          <div
            v-else
            class="flex min-h-0 flex-1 flex-col"
          >
            <div class="flex min-h-0 w-full flex-1 overflow-auto rounded-lg border border-gray-200">
              <div class="relative h-full w-full flex-1 p-4">
                <div class="flex h-full items-start justify-center overflow-y-auto">
                  <div class="pdf-container">
                    <VuePDF
                      ref="vuePDFRef"
                      :pdf="pdf"
                      class="pdf"
                      on-layer
                      text-layer
                      :scale="1.2"
                      :page="currentPage || 1"
                      @loaded="onPdfLoaded"
                      @text-loaded="onPdfLoaded"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="pdfLoaded && pdf"
              class="mt-3 flex items-center justify-between"
            >
              <GenericPdfNav
                :current-page="currentPage || 1"
                :total-pages="pages || 1"
                @update-page="setPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .pdf-container {
    max-width: 100%;
    overflow: auto;
  }

  .w-3\/10 {
    width: 30%;
  }

  @media (min-width: 768px) {
    .md\:w-7\/10 {
      width: 70%;
    }
  }
</style>
